import React, { useState } from "react";
import { Contract, parseUnits } from "ethers";
import { useWallet } from "../context/WalletContext";
import { TOKEN, CHAIN } from "../mock";
import { STAMP_TOKEN, TOKEN_CONFIGURED, BURN_ADDRESS, ERC20_ABI, API, NET, explorerTx } from "../config";
import { WavyRule } from "./Guilloche";
import { toast } from "sonner";
import { Wallet, Loader2, Copy, Check, AlertTriangle, ExternalLink } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("en-US");

const WARNINGS = [
  { t: "Burning is permanent", b: "There is no undo, no reversal and no refund. If you burn the wrong amount, it is gone." },
  { t: "Never use an exchange address", b: "The certificate is delivered to a Zcash address you control. An exchange cannot receive an inscription." },
  { t: "The number is exact", b: "The certificate records exactly what you destroyed \u2014 not a rounded figure, not a balance." },
  { t: "Nobody holds your funds", b: "There is no escrow and no custody. The moment you sign, the tokens stop existing." },
];

export default function BurnForm() {
  const { address, short, connecting, connect, balance, bnb, decimals, chainOk, getProvider, refresh } = useWallet();
  const [zMode, setZMode] = useState("new");
  const [zAddr, setZAddr] = useState("");
  const [amount, setAmount] = useState("");
  const [burning, setBurning] = useState(false);
  const [copied, setCopied] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const [generatedZ] = useState(() => "t1" + Array.from({ length: 33 }, () => "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz123456789"[Math.floor(Math.random() * 57)]).join(""));
  const destination = zMode === "new" ? generatedZ : zAddr;
  const amt = parseInt((amount || "").replace(/[^0-9]/g, "") || "0", 10);
  const valid = TOKEN_CONFIGURED && address && chainOk && amt >= TOKEN.minBurn && (zMode === "new" || zAddr.trim().length > 20) && amt <= balance;

  const handleBurn = async () => {
    if (!valid) return;
    setBurning(true);
    try {
      const provider = getProvider();
      const signer = await provider.getSigner();
      const token = new Contract(STAMP_TOKEN, ERC20_ABI, signer);
      const value = parseUnits(String(amt), decimals);
      toast("Confirm the burn in your wallet\u2026");
      const tx = await token.transfer(BURN_ADDRESS, value);
      toast("Broadcasting\u2026", { description: tx.hash.slice(0, 18) + "\u2026" });
      await tx.wait();

      // record + get Zcash certificate from backend
      let inscription = null, mode = "mock";
      try {
        const res = await fetch(`${API}/certificates`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tx_hash: tx.hash, amount: amt, token_symbol: TOKEN.symbol, burner: address, zcash_address: destination, zcash_mode: zMode }),
        });
        const data = await res.json();
        inscription = data.inscription; mode = data.inscription_mode;
      } catch (e) { /* backend optional */ }

      setReceipt({ amt, tx: tx.hash, inscription, mode, dest: destination });
      await refresh();
      toast.success("STAMP destroyed", { description: `${fmt(amt)} ${TOKEN.symbol} burned on ${NET.name}` });
    } catch (e) {
      toast.error("Burn failed", { description: e?.shortMessage || e?.message || "Transaction rejected" });
    } finally {
      setBurning(false);
    }
  };

  return (
    <section id="burn" className="container-x py-10 md:py-14">
      <div className="paper paper-grain rounded-sm p-7 md:p-12">
        <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-14">
          <div>
            <h2 className="font-number font-semibold" style={{ fontSize: "clamp(1.6rem,3.4vw,2.3rem)" }}>Issue a certificate</h2>
            <WavyRule className="my-6" />

            {!TOKEN_CONFIGURED && (
              <div className="mb-6 px-4 py-3 rounded-sm text-[13px]" style={{ background: "rgba(227,179,65,0.08)", border: "1px solid rgba(227,179,65,0.35)", color: "var(--text)" }}>
                <b>Token not set yet.</b> Real burns are disabled until <code className="font-mono">REACT_APP_STAMP_TOKEN</code> (the STAMP BEP-20 address on {NET.name}) is filled in <code className="font-mono">frontend/.env</code>. Everything else — wallet, network, Zcash certificate — works.
              </div>
            )}

            {receipt ? (
              <div className="fade-up">
                <div className="label" style={{ color: "var(--green)" }}>Certificate issued</div>
                <div className="font-number font-semibold mt-2" style={{ fontSize: "2.4rem" }}>{fmt(receipt.amt)} <span className="text-[1rem] font-mono" style={{ color: "var(--muted)" }}>STAMP</span></div>
                <div className="mt-5 flex flex-col gap-3">
                  <Field label={`Burn tx (${NET.name})`} value={receipt.tx} link={explorerTx(receipt.tx)} />
                  <Field label="Zcash inscription" value={receipt.inscription || "pending"} />
                  <Field label="Delivered to" value={receipt.dest} />
                </div>
                <p className="mt-5 label" style={{ letterSpacing: "0.04em" }}>
                  Burn is REAL on {NET.name}. Zcash inscription is {receipt.mode === "mock" || receipt.mode === "mock_fallback" ? "MOCKED (fill Zcash node in backend .env to make real)" : "REAL"}.
                </p>
                <button onClick={() => { setReceipt(null); setAmount(""); }} className="mt-6 h-11 px-6 nav-link" style={{ background: "transparent", border: "1px solid var(--border-strong)", color: "var(--text)", fontSize: 12 }}>Issue another</button>
              </div>
            ) : (
              <div className="flex flex-col gap-8">
                <Step n="1" title={`Your ${NET.name} wallet`}>
                  {address ? (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-mono text-[13px]">{short} <span style={{ color: "var(--muted)" }}>· {fmt(balance)} STAMP · {bnb.toFixed(4)} {NET.symbol}</span></span>
                      <span className="label" style={{ color: chainOk ? "var(--green)" : "var(--red)" }}>{chainOk ? "connected" : "wrong network"}</span>
                    </div>
                  ) : (
                    <button onClick={connect} disabled={connecting}
                      className="inline-flex items-center gap-2 h-11 px-5 nav-link" style={{ background: "var(--red)", color: "#fff", fontSize: 12 }}>
                      {connecting ? <Loader2 size={14} className="animate-spin" /> : <Wallet size={14} />} Connect wallet
                    </button>
                  )}
                </Step>

                <Step n="2" title="Where the certificate goes">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Toggle active={zMode === "new"} onClick={() => setZMode("new")}>Create a Zcash address for me</Toggle>
                    <Toggle active={zMode === "existing"} onClick={() => setZMode("existing")}>I already have one</Toggle>
                  </div>
                  {zMode === "new" ? (
                    <div className="flex items-center justify-between gap-2 px-3 h-11" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>
                      <span className="font-mono text-[12px] truncate">{generatedZ}</span>
                      <button onClick={() => { navigator.clipboard?.writeText(generatedZ); setCopied(true); setTimeout(() => setCopied(false), 1200); }} className="shrink-0" style={{ color: "var(--muted)" }}>
                        {copied ? <Check size={15} style={{ color: "var(--gold)" }} /> : <Copy size={15} />}
                      </button>
                    </div>
                  ) : (
                    <input value={zAddr} onChange={(e) => setZAddr(e.target.value)} placeholder="t1\u2026 your transparent Zcash address"
                      className="w-full px-3 h-11 font-mono text-[13px] outline-none" style={{ background: "var(--bg-2)", border: "1px solid var(--border)", color: "var(--text)" }}
                      onFocus={(e) => (e.target.style.borderColor = "var(--gold)")} onBlur={(e) => (e.target.style.borderColor = "var(--border)")} />
                  )}
                </Step>

                <Step n="3" title="How much STAMP to destroy">
                  <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9,]/g, ""))} placeholder={`Minimum ${fmt(TOKEN.minBurn)}`}
                    className="w-full px-3 h-12 font-number text-[20px] outline-none" style={{ background: "var(--bg-2)", border: "1px solid var(--border)", color: "var(--text)" }}
                    onFocus={(e) => (e.target.style.borderColor = "var(--gold)")} onBlur={(e) => (e.target.style.borderColor = "var(--border)")} />
                  <div className="mt-2 text-[12.5px]" style={{ color: amt > 0 && amt < TOKEN.minBurn ? "var(--red)" : "var(--muted)" }}>
                    {amt > balance && address && TOKEN_CONFIGURED ? "Amount exceeds your balance." : `Minimum ${fmt(TOKEN.minBurn)}. The certificate records this number exactly.`}
                  </div>
                </Step>

                <div>
                  <button onClick={handleBurn} disabled={!valid || burning}
                    className="h-[52px] px-8 nav-link flex items-center justify-center gap-2 transition-all disabled:opacity-40"
                    style={{ background: valid ? "var(--red)" : "var(--card-2)", color: valid ? "#fff" : "var(--muted)", border: valid ? "none" : "1px solid var(--border)", fontSize: 13, letterSpacing: "0.14em" }}>
                    {burning ? <><Loader2 size={16} className="animate-spin" /> Destroying…</> : "Destroy STAMP"}
                  </button>
                  <p className="mt-3 text-[12.5px]" style={{ color: "var(--muted)" }}>This cannot be undone, reversed or refunded. Your tokens stop existing the moment you sign.</p>
                </div>
              </div>
            )}
          </div>

          <aside className="lg:perf-v lg:pl-14">
            <h3 className="font-number font-semibold" style={{ fontSize: "1.5rem" }}>Before you sign</h3>
            <div className="mt-6 flex flex-col gap-6">
              {WARNINGS.map((w, i) => (
                <div key={i}>
                  <div className="flex items-center gap-2 font-medium text-[14.5px]"><AlertTriangle size={14} style={{ color: "var(--gold)" }} /> {w.t}</div>
                  <p className="mt-1.5 text-[13px] leading-relaxed" style={{ color: "var(--muted)" }}>{w.b}</p>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function Step({ n, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <span className="label" style={{ color: "var(--gold)" }}>{n}</span>
        <span className="label" style={{ color: "var(--text)" }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function Toggle({ active, onClick, children }) {
  return (
    <button onClick={onClick} className="px-3 h-9 text-[12.5px] transition-colors"
      style={{ background: active ? "rgba(227,179,65,0.1)" : "var(--bg-2)", border: active ? "1px solid rgba(227,179,65,0.4)" : "1px solid var(--border)", color: active ? "var(--gold)" : "var(--muted)" }}>
      {children}
    </button>
  );
}

function Field({ label, value, link }) {
  return (
    <div className="px-3 py-2.5" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>
      <div className="label mb-1 flex items-center gap-1.5">{label} {link && <a href={link} target="_blank" rel="noreferrer" className="gold-text"><ExternalLink size={11} /></a>}</div>
      <div className="font-mono text-[11.5px] break-all" style={{ color: "var(--text)" }}>{value}</div>
    </div>
  );
}
